import { DataSource, LookerAppInfo } from "@coeff/api";
import { COLORS, ERROR_TITLES, WARNING_MESSAGES, WARNING_TITLES } from "@coeff/utils";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { FlexColumn, FlexRow } from "../../../common";
import { Button } from "../../../common/Button";
import { Card } from "../../../common/Card";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { Loader } from "../../../common/Loader";
import { Select, Option } from "../../../common/Select";
import { WarningMessage } from "../../../common/WarningMessage";

import { getLookerBaseUrl } from "./AuthorizeLookerContentV2";
import { LOOKER_CONFIG_WEBAPP_URL, OAUTH_NOTICE } from "./constants";
import { useOAuth } from "./useOAuth.hook";

const StyledInput = styled.label`
  display: block;
  width: auto;
  margin: 16px;

  .coeff__input {
    height: 32px;
    margin: auto;
    border-color: ${COLORS.black15};
  }
`;

const StyledSelect = styled(Select)`
  && {
    width: 100%;

    &&.ant-select {
      margin: 10px 0 0 0 !important;
    }

    .ant-select-selector {
      border-color: ${COLORS.black15} !important;
      height: 32px !important;

      .ant-select-selection-item,
      .ant-select-selection-placeholder {
        line-height: 28px !important;
      }
    }
`;

const NEW_CONNECTION = -1;

export const OAuthConnections: React.FC<{
  apps: LookerAppInfo[];
  event_props: { email?: string };
  webappBaseUrl?: string;
  onAddNew: () => void;
  onAuthorize: (dataSource: DataSource) => void;
}> = ({ apps, event_props, webappBaseUrl = "", onAddNew, onAuthorize }) => {
  const lookerConfigWebappUrl = `${webappBaseUrl}${LOOKER_CONFIG_WEBAPP_URL}`;

  const [selected, setSelected] = useState<number>();
  const [error, setError] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const { handleLookerAuthorize, handleLookerCancel, isAuthInProgress, popupBlocked } = useOAuth({
    event_props,
    onAuthorize,
    onError: setOauthError,
  });

  useEffect(() => {
    if (apps.length === 1) {
      setSelected(0);
    }
  }, [apps.length]);

  const handleClick = () => {
    if (selected === undefined || selected === null) {
      setError("Please select an account");
      return;
    }
    setError(null);

    if (selected === NEW_CONNECTION) {
      onAddNew();
      return;
    }

    const appInfo = apps[selected];
    if (appInfo) {
      handleLookerAuthorize({ appInfo });
    }
  };

  const onCancel = () => {
    handleLookerCancel();
  };

  return (
    <>
      <Typography
        color="textSecondary"
        fontWeight={600}
        fontSize={14}
        lineHeight={"18px"}
        style={{
          textAlign: "center",
          marginTop: 10,
          padding: "0 20px",
        }}
      >
        {OAUTH_NOTICE.title}
      </Typography>
      {popupBlocked && (
        <WarningMessage
          title={WARNING_TITLES.POPUP_BLOCKED}
          message={WARNING_MESSAGES.POPUP_BLOCKED}
        />
      )}
      {oauthError && (
        <ErrorMessage
          screen="AuthorizeLookerV2"
          title={oauthError.title}
          message={oauthError.message}
          onClose={() => setOauthError(null)}
        />
      )}
      <StyledInput>
        Account
        <StyledSelect
          placeholder="Choose..."
          className={error ? "error" : ""}
          value={selected}
          error={!!error}
          onSelect={value => {
            setSelected(value);
            if (value === NEW_CONNECTION) {
              onAddNew();
            }
            setError(null);
          }}
          virtual={apps?.length > 50}
        >
          {apps.map((app, index) => (
            <Option key={index} value={index}>
              {getLookerBaseUrl(app.base_url)}
            </Option>
          ))}
          <Option value={NEW_CONNECTION}>
            <Typography color="textLink">+ Add new account</Typography>
          </Option>
        </StyledSelect>
        <FlexColumn
          style={{ marginTop: 30, justifyContent: "center", alignContent: "center" }}
          gap={0.5}
        >
          <Button type="primary" disabled={isAuthInProgress} onClick={handleClick}>
            <FlexRow>
              {isAuthInProgress ? (
                <Loader
                  color="white"
                  size="micro"
                  style={{
                    position: "relative",
                    top: "2px",
                  }}
                />
              ) : (
                "Authorize"
              )}
            </FlexRow>
          </Button>

          {isAuthInProgress ? (
            <Button type="link" onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            <Button type="link"></Button>
          )}
        </FlexColumn>
      </StyledInput>

      <Card style={{ padding: 12, marginTop: 30, textAlign: "center" }}>
        <Typography style={{ paddingTop: 8 }}>
          This connection uses your domain's Looker OAuth settings.
        </Typography>
        <Typography color="textLink" style={{ padding: 8 }}>
          <a target="_blank" href={lookerConfigWebappUrl}>
            Manage OAuth Settings
          </a>
        </Typography>
      </Card>
    </>
  );
};
